import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, NavLink } from 'react-router-dom'
import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn, useClerk, useUser } from '@clerk/clerk-react'
import Dashboard from './pages/Dashboard'
import ClientPortal from './pages/ClientPortal'
import DocumentInbox from './pages/DocumentInbox'
import BankStatementPL from './modules/BankStatementPL'
import WhatsAppEngine from './modules/WhatsAppEngine'
import DeadlineTracker from './modules/DeadlineTracker'
import VATReturn from './modules/VATReturn'
import CT600 from './modules/CT600'
import AnnualAccounts from './modules/AnnualAccounts'
import RTIPayroll from './modules/RTIPayroll'
import ConfirmationStatement from './modules/ConfirmationStatement'
import SA100 from './modules/SA100'
import CompanyFormation from './modules/CompanyFormation'
import './index.css'

const CLERK_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY

const NAV = [
  { section: 'Overview' },
  { label: 'Dashboard', path: '/', icon: '⊞' },
  { label: 'Clients', path: '/clients', icon: '◎', badge: '100+', badgeClass: 'green' },
  { section: 'AI Modules' },
  { label: 'Deadline Tracker', path: '/deadlines', icon: '◈', badge: '3', badgeClass: 'red' },
  { label: 'WhatsApp Engine', path: '/whatsapp', icon: '◉', badge: '6' },
  { label: 'Bank Stmt → P&L', path: '/bank-pl', icon: '⬡' },
  { label: 'VAT Return', path: '/vat', icon: '◧' },
  { label: 'CT600 Corp Tax', path: '/ct600', icon: '◨' },
  { label: 'RTI Payroll', path: '/payroll', icon: '◪' },
  { label: 'Self Assessment', path: '/self-assessment', icon: '◫' },
  { label: 'Annual Accounts', path: '/annual-accounts', icon: '◬' },
  { label: 'Conf. Statement', path: '/confirmation', icon: '◭' },
  { section: 'Admin' },
  { label: 'Company Formation', path: '/formation', icon: '◮' },
  { label: 'Document Vault', path: '/vault', icon: '▣' },
]

function Placeholder({ title }) {
  return (
    <div className="placeholder-page">
      <div style={{ fontSize: 40, opacity: .3 }}>🔜</div>
      <h2>{title}</h2>
      <p>This module is built and ready — connecting to the live backend in the next update.</p>
    </div>
  )
}

function Layout({ children, title }) {
  const { signOut } = useClerk()
  const { user } = useUser()
  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sb-logo">
          <div className="sb-icon">S</div>
          <div>
            <div className="sb-name">Sunshoos</div>
            <div className="sb-ver">AI Agent · v2.0</div>
          </div>
        </div>
        <nav className="sb-nav">
          {NAV.map((item, i) =>
            item.section
              ? <div key={i} className="sb-section">{item.section}</div>
              : (
                <NavLink key={i} to={item.path} end
                  className={({ isActive }) => 'sb-link' + (isActive ? ' active' : '')}>
                  <span style={{ fontSize: 13, width: 18, textAlign: 'center', flexShrink: 0 }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge && <span className={`sb-badge${item.badgeClass ? ' ' + item.badgeClass : ''}`}>{item.badge}</span>}
                </NavLink>
              )
          )}
        </nav>
        <div className="sb-footer">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'rgba(201,151,58,0.1)', border: '1px solid rgba(201,151,58,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#C9973A', flexShrink: 0 }}>
              {(user?.firstName?.[0] || 'S')}{(user?.lastName?.[0] || 'A')}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.75)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.firstName || 'Sunshoos'} {user?.lastName || 'Admin'}
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>IFA Accredited</div>
            </div>
            <button className="sign-out-btn" onClick={() => signOut()}>Out</button>
          </div>
        </div>
      </aside>

      <header className="topbar">
        <div className="topbar-title">{title}</div>
      </header>

      <main className="main-content">
        {children}
      </main>
    </div>
  )
}

function Protected({ children, title }) {
  return (
    <>
      <SignedIn><Layout title={title}>{children}</Layout></SignedIn>
      <SignedOut><RedirectToSignIn /></SignedOut>
    </>
  )
}

export default function App() {
  return (
    <ClerkProvider publishableKey={CLERK_KEY}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Protected title="Dashboard"><Dashboard /></Protected>} />
          <Route path="/bank-pl" element={<Protected title="Bank Stmt → P&L"><BankStatementPL /></Protected>} />
          <Route path="/clients" element={<Protected title="Clients"><Placeholder title="Client Database" /></Protected>} />
          <Route path="/deadlines" element={<Protected title="Deadline Tracker"><DeadlineTracker /></Protected>} />
          <Route path="/whatsapp" element={<Protected title="WhatsApp Engine"><WhatsAppEngine /></Protected>} />
          <Route path="/vat" element={<Protected title="VAT Return"><VATReturn /></Protected>} />
          <Route path="/ct600" element={<Protected title="CT600 Corp Tax"><CT600 /></Protected>} />
          <Route path="/payroll" element={<Protected title="RTI Payroll"><RTIPayroll /></Protected>} />
          <Route path="/self-assessment" element={<Protected title="Self Assessment"><SA100 /></Protected>} />
          <Route path="/annual-accounts" element={<Protected title="Annual Accounts"><AnnualAccounts /></Protected>} />
          <Route path="/confirmation" element={<Protected title="Conf. Statement"><ConfirmationStatement /></Protected>} />
          <Route path="/formation" element={<Protected title="Company Formation"><CompanyFormation /></Protected>} />
          <Route path="/vault" element={<Protected title="Document Vault"><DocumentInbox /></Protected>} />
          <Route path="/portal" element={<ClientPortal />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </ClerkProvider>
  )
}
